import React, { createContext, useContext, useState, useCallback, useMemo } from "react";

/**
 * ExpandedFieldContext
 * 
 * Manages which expandable field is currently open in the edit panel.
 * Only one field can be expanded at a time (accordion behavior).
 */
export const ExpandedFieldContext = createContext();

export const useExpandedField = () => {
  const context = useContext(ExpandedFieldContext);
  if (!context) {
    throw new Error("useExpandedField must be used within an ExpandedFieldProvider");
  }
  return context;
};

export const ExpandedFieldProvider = ({ children }) => {
  // Track the currently expanded field by a unique identifier
  // Format: "fieldType:fieldId" or "fieldType:fieldName"
  const [expandedFieldId, setExpandedFieldId] = useState(null);

  const setExpanded = useCallback((fieldId) => {
    setExpandedFieldId((current) => {
      // If clicking the same field, toggle it closed
      if (current === fieldId) {
        return null;
      }
      // Otherwise, expand the new field (closing any previously expanded one)
      return fieldId;
    });
  }, []);

  const isExpanded = useCallback((fieldId) => {
    return expandedFieldId === fieldId;
  }, [expandedFieldId]);

  const value = useMemo(() => ({
    expandedFieldId,
    setExpanded,
    isExpanded,
  }), [expandedFieldId, setExpanded, isExpanded]);

  return (
    <ExpandedFieldContext.Provider value={value}>
      {children}
    </ExpandedFieldContext.Provider>
  );
};
